import {
  expenseCategoryListSchema,
  expenseCategorySchema,
  expensePageSchema,
  expenseSchema,
  pendingExpensesSchema,
  pnlRevenueEntriesSchema,
  pnlSchema,
  recurringExpenseListSchema,
  recurringExpenseSchema,
  type ConfirmPendingExpenseInput,
  type CreateExpenseCategoryInput,
  type CreateRecurringExpenseInput,
  type Expense,
  type ExpenseCategory,
  type ExpenseCategoryList,
  type ExpensePage,
  type PendingExpenses,
  type Pnl,
  type PnlBasis,
  type PnlRevenueEntries,
  type RecordExpenseInput,
  type RecurringExpense,
  type RecurringExpenseList,
  type UpdateExpenseCategoryInput,
  type UpdateExpenseInput,
  type UpdateRecurringExpenseInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every expense and P&L call the app makes.
 *
 * The drill-down is why `ExpenseListParams` carries `month` and `basis` rather than a pair of
 * dates: the P&L computes its figures from exactly those two parameters, so a screen that taps a
 * category total and asks for the rows behind it is asking the server the SAME question, not a
 * second one written to resemble it.
 *
 * Note what is absent for a system-generated row: there is no separate "edit payout expense".
 * The API refuses it with a 409, so offering one here would be offering a button that cannot
 * work — the payout is what gets corrected.
 */

const EXPENSES = '/expenses';

export interface ExpenseListParams {
  month?: string;
  basis?: PnlBasis;
  categoryId?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

function listQuery(params: ExpenseListParams): Record<string, string | number> {
  return {
    ...(params.month ? { month: params.month } : {}),
    basis: params.basis ?? 'accrual',
    ...(params.categoryId ? { categoryId: params.categoryId } : {}),
    ...(params.q ? { q: params.q } : {}),
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
  };
}

export async function getExpenses(
  client: ApiClient,
  params: ExpenseListParams = {},
): Promise<ExpensePage> {
  return await client.get(EXPENSES, { schema: expensePageSchema, query: listQuery(params) });
}

/** The same list as a file. The browser is sent to the API, which answers with a CSV body. */
export function expensesCsvPath(params: ExpenseListParams = {}): string {
  const query = new URLSearchParams(
    Object.entries({ ...listQuery(params), format: 'csv' }).map(([key, value]) => [
      key,
      String(value),
    ]),
  );
  return `${EXPENSES}?${query.toString()}`;
}

export async function recordExpense(
  client: ApiClient,
  input: RecordExpenseInput,
): Promise<Expense> {
  return await client.post(EXPENSES, input, { schema: expenseSchema });
}

export async function updateExpense(
  client: ApiClient,
  expenseId: string,
  input: UpdateExpenseInput,
): Promise<Expense> {
  return await client.patch(`${EXPENSES}/${expenseId}`, input, { schema: expenseSchema });
}

export async function deleteExpense(client: ApiClient, expenseId: string): Promise<void> {
  await client.del(`${EXPENSES}/${expenseId}`);
}

// --- Categories ---------------------------------------------------------------------------

export async function getExpenseCategories(client: ApiClient): Promise<ExpenseCategoryList> {
  return await client.get(`${EXPENSES}/categories`, { schema: expenseCategoryListSchema });
}

export async function createExpenseCategory(
  client: ApiClient,
  input: CreateExpenseCategoryInput,
): Promise<ExpenseCategory> {
  return await client.post(`${EXPENSES}/categories`, input, { schema: expenseCategorySchema });
}

export async function updateExpenseCategory(
  client: ApiClient,
  categoryId: string,
  input: UpdateExpenseCategoryInput,
): Promise<ExpenseCategory> {
  return await client.patch(`${EXPENSES}/categories/${categoryId}`, input, {
    schema: expenseCategorySchema,
  });
}

// --- Standing instructions ---------------------------------------------------------------

export async function getRecurringExpenses(client: ApiClient): Promise<RecurringExpenseList> {
  return await client.get(`${EXPENSES}/recurring`, { schema: recurringExpenseListSchema });
}

export async function createRecurringExpense(
  client: ApiClient,
  input: CreateRecurringExpenseInput,
): Promise<RecurringExpense> {
  return await client.post(`${EXPENSES}/recurring`, input, { schema: recurringExpenseSchema });
}

export async function updateRecurringExpense(
  client: ApiClient,
  recurringId: string,
  input: UpdateRecurringExpenseInput,
): Promise<RecurringExpense> {
  return await client.patch(`${EXPENSES}/recurring/${recurringId}`, input, {
    schema: recurringExpenseSchema,
  });
}

export async function deleteRecurringExpense(
  client: ApiClient,
  recurringId: string,
): Promise<void> {
  await client.del(`${EXPENSES}/recurring/${recurringId}`);
}

/** Reading this is what materialises anything a standing instruction has produced. */
export async function getPendingExpenses(client: ApiClient): Promise<PendingExpenses> {
  return await client.get(`${EXPENSES}/pending`, { schema: pendingExpensesSchema });
}

export async function confirmPendingExpense(
  client: ApiClient,
  expenseId: string,
  input: ConfirmPendingExpenseInput = {},
): Promise<Expense> {
  return await client.post(`${EXPENSES}/pending/${expenseId}/confirm`, input, {
    schema: expenseSchema,
  });
}

export async function dismissPendingExpense(client: ApiClient, expenseId: string): Promise<void> {
  await client.post(`${EXPENSES}/pending/${expenseId}/dismiss`, {});
}

// --- Profit and loss -------------------------------------------------------------------------

export interface PnlParams {
  month: string;
  basis: PnlBasis;
}

export async function getPnl(client: ApiClient, params: PnlParams): Promise<Pnl> {
  return await client.get(`${EXPENSES}/pnl`, { schema: pnlSchema, query: { ...params } });
}

export async function getPnlRevenueEntries(
  client: ApiClient,
  params: PnlParams,
): Promise<PnlRevenueEntries> {
  return await client.get(`${EXPENSES}/pnl/revenue`, {
    schema: pnlRevenueEntriesSchema,
    query: { ...params },
  });
}

export function pnlCsvPath(params: PnlParams): string {
  const query = new URLSearchParams({ ...params, format: 'csv' });
  return `${EXPENSES}/pnl?${query.toString()}`;
}
